import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { Conditions } from '@vsolv/packages/conditions/domain';
import { Property } from '@vsolv/packages/properties/domain';
import { PropertyService } from '@vsolv/packages/properties/web';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { Policy } from '@wsphere/warranties/domain';
import { BehaviorSubject, combineLatest, shareReplay, switchMap, tap } from 'rxjs';
import { v4 } from 'uuid';
import { POLICY_TERM_AUTO_RENEW_CONFIG } from '../../config/policy-term-auto-renew.config';
import { PolicyService } from '../../services';

function wholeNumberValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;

  if (value === null || value === undefined || value === '') {
    return null;
  }

  return Number.isInteger(Number(value)) ? null : { wholeNumber: true };
}

export interface PolicyTerm {
  id: string;
  title: string;

  years: number;
  months: number;
  days: number;
  durationPropertyPath: string | null;

  order: number;

  paymentOptional: boolean;
  autoRenew: boolean;
  maxAutoRenewals: number | string | null;

  installments: 0;
  percentage: number;
  installmentName: string;
  installmentsCheckBox: boolean;
  oneTimeCheckBox: boolean;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-policy-term',
  templateUrl: './policy-term.component.html',
  standalone: false,
})
export class PolicyTermComponent {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private toastSvc: ToastService,
    private policySvc: PolicyService,
    private formBuilder: FormBuilder,
    private propertySvc: PropertyService
  ) {}

  readonly config = POLICY_TERM_AUTO_RENEW_CONFIG;

  num = Conditions.PropertyType.NUMBER;

  @PropertyListener('policy') policy$ = new BehaviorSubject<Policy.Model | null>(null);
  @Input() policy: Policy.Model | null = null;

  @PropertyListener('editing') editing$ = new BehaviorSubject<boolean>(false);
  @Input() editing = false;

  @Input() value: Policy.PolicyTerm[] | null = null;

  @PropertyListener('propertySetId') propertySetId$ = new BehaviorSubject<string | null>(null);
  @Input() propertySetId: string | null = null;

  @Output() closed = new EventEmitter();
  @Output() prevPage = new EventEmitter<Policy.PolicyTerm[]>();
  @Output() pageCompleted = new EventEmitter<Policy.PolicyTerm[]>();

  saving = false;

  draft = Policy.Status.DRAFT;

  terms: FormGroup[] = [];

  propertySet$ = combineLatest([this.policy$, this.propertySetId$]).pipe(
    switchMap(async ([policy, propertySetId]) => {
      const id = propertySetId ?? policy?.propertySetId;
      if (!id) return null;
      return await this.propertySvc.retrievePropertySet(id);
    }),
    shareReplay(1)
  );

  patchValue$ = combineLatest([this.policy$, this.editing$]).pipe(
    tap(([policy, editing]) => this.loadForm(policy, editing))
  );

  close() {
    // Reload from the current inputs instead of overwriting them: the dialog's [policy]/[editing]
    // bindings don't change between opens, so Angular would never set them back and the next Edit
    // would reopen in create mode with a blank form.
    this.reload();

    this.closed.emit();
  }

  /** Reload the form from the current inputs, dropping any unsaved edits. */
  reload() {
    this.loadForm(this.policy, this.editing);
  }

  /** Edit mode shows the policy's saved terms; create mode starts blank, or from the wizard's value. */
  private loadForm(policy: Policy.Model | null, editing: boolean) {
    if (!editing) {
      this.mapTermsToForm([]);

      if (this.value) {
        this.mapTermsToForm(this.value);
      }
    } else {
      if (!policy) return;

      this.mapTermsToForm(policy?.terms ?? []);
    }
  }

  goBack() {
    const terms = this.mapFormToTerms();
    this.prevPage.emit(terms);
  }

  pageComplete() {
    const terms = this.mapFormToTerms();
    this.pageCompleted.emit(terms);
  }

  newTerm() {
    this.terms.push(
      this.formBuilder.group({
        id: [null],
        title: [null as string | null, [Validators.required]],
        years: [0, [Validators.min(0), wholeNumberValidator]],
        months: [0, [Validators.min(0), wholeNumberValidator]],
        days: [0, [Validators.min(0), wholeNumberValidator]],
        durationPropertyPath: [null as string | null],
        order: [(this.terms.length ? null : 0) as number | null, [Validators.required, Validators.min(0)]],
        paymentOptional: [false],
        autoRenew: [false],
        maxAutoRenewals: [null as number | null, [Validators.min(0), wholeNumberValidator]],
        oneTimeCheckBox: [false],
        installmentsCheckBox: [false],
        schedules: this.formBuilder.array([]),
        expanded: [true],
      })
    );
  }

  scheduleFormControl(installments: number, percentage: number, installmentName: string) {
    return this.formBuilder.group({
      installments: [installments, [Validators.min(0)]],
      percentage: [percentage, [Validators.min(-100), Validators.max(100)]],
      installmentName: [installmentName, [Validators.maxLength(25)]],
    });
  }

  toggleTerm(index: number) {
    this.terms[index].patchValue({ expanded: !(this.terms[index].value.expanded ?? false) });
  }

  trackByFn = (index: number) => index;

  getTermDurationDays(index: number) {
    const value = this.terms[index]?.value;
    return (value?.years || 0) * 365 + (value?.months || 0) * 30 + (value?.days || 0);
  }

  isTermValid(index: number) {
    const policyTermForm = this.terms[index];

    const value = policyTermForm.value;
    const maxInstallments = this.getTermDurationDays(index);

    if (
      policyTermForm.invalid ||
      !value.title ||
      value.order === null ||
      value.order === undefined ||
      value.order === '' ||
      value.order < 0 ||
      (!value?.durationPropertyPath && !value?.years && !value?.months && !value?.days) ||
      (value?.installmentsCheckBox &&
        value.schedules.some(
          (schedule: { installments: number; percentage: number; installmentName: string }) =>
            !schedule.installments ||
            schedule.installments < 2 ||
            (!value?.durationPropertyPath && schedule.installments > maxInstallments) ||
            schedule.percentage < -100 ||
            schedule.percentage > 100 ||
            !schedule.installmentName ||
            schedule.installmentName.length > 25
        ))
    ) {
      return false;
    }
    return true;
  }

  areTermsValid() {
    let touched = false;

    if (this.policy && this.policy.terms.length !== this.terms.length) {
      touched = true;
    }

    for (let index = 0; index < this.terms.length; index++) {
      const policyTermForm = this.terms[index];

      if (policyTermForm.dirty || policyTermForm.touched) touched = true;

      const valid = this.isTermValid(index);

      if (!valid) return false;
    }

    return touched;
  }

  deleteTerm(index: number) {
    this.terms.splice(index, 1);
  }

  setTermDurationPropertyPath(
    index: number,
    property: { property: Property.Model<Property.PropertyType> | Conditions.Property; propertyPath: string } | null
  ) {
    this.terms[index].patchValue({ durationPropertyPath: property?.propertyPath ?? null });
    this.terms[index].markAsDirty();
  }

  oneTimeCheckBox(index: number) {
    const termForm = this.terms[index];
    termForm.patchValue({ oneTimeCheckBox: !termForm.value.oneTimeCheckBox });
    termForm.markAsDirty();
  }

  installmentsCheckBox(index: number) {
    const termForm = this.terms[index];
    termForm.patchValue({ installmentsCheckBox: !termForm.value.installmentsCheckBox });
    termForm.markAsDirty();
  }

  getInstallments(index: number, installments: number) {
    // Matches the actual billing cadence: preview.handler.ts spaces installments via
    // moment's .add(duration / installments, 'days'), which rounds the fractional day
    // count to the nearest whole day rather than flooring it.
    return Math.round(this.getTermDurationDays(index) / (installments || 1));
  }

  async save() {
    if (!this.policy) return;

    this.saving = true;

    const terms = this.mapFormToTerms();

    const policy = await this.policySvc
      .update(this.policy.id, {
        canCreateNewVersion: true,
        terms,
      })
      .catch(({ error }) => {
        this.toastSvc.show({
          type: 'error',
          title: 'Something went wrong',
          text: error.message,
        });

        // The editor is reused on the next open, so a failed save must not leave Save disabled.
        this.saving = false;

        this.close();
      });

    if (policy) {
      if (this.policy.id === policy.id) {
        this.toastSvc.show({
          type: 'success',
          title: 'Updated policy',
          text: '<strong>' + this.policy.title + '</strong> has been successfully updated.',
        });

        this.policySvc.refreshPolicy();
      } else {
        this.toastSvc.show({
          type: 'success',
          title: 'New policy version created',
          text: 'An updated version of <strong>' + this.policy.title + '</strong> has been successfully created.',
        });

        this.policySvc.refreshPolicy(policy.id);
        this.policySvc.clearPlan();

        this.navigateTo('../' + policy.id);
      }

      this.saving = false;
      this.close();
    }
  }

  addSchedule(term: FormGroup) {
    const schedules = term.get('schedules') as FormArray;
    schedules.push(this.scheduleFormControl(0, 0, ''));
  }

  removeSchedule(term: FormGroup, schedule: FormGroup) {
    const schedules = term.get('schedules') as FormArray;
    schedules.removeAt(schedules.controls.indexOf(schedule));
  }

  private mapFormToTerms() {
    const terms: Policy.PolicyTerm[] = [];

    for (const termForm of this.terms) {
      const policyTerm = termForm.value;
      const paymentSchedules: Policy.PaymentSchedule[] = [];

      if (policyTerm.installmentsCheckBox) {
        for (const schedule of policyTerm.schedules) {
          if (schedule.installments) {
            paymentSchedules.push({
              id: 'psc_' + v4().replace(/-/g, ''),
              installments: schedule.installments,
              rate: schedule.percentage || 0,
              title: schedule.installmentName,
            });
          }
        }
      }

      if (policyTerm.oneTimeCheckBox && policyTerm.oneTimeCheckBox) {
        paymentSchedules.push({
          id: 'psc_' + v4().replace(/-/g, ''),
          installments: 1,
          rate: 0,
          title: 'One Time Payment',
        });
      }

      const staticDuration = (policyTerm.years || 0) * 365 + (policyTerm.months || 0) * 30 + (policyTerm.days || 0);
      // When a dynamic path is set, duration is resolved at checkout; send 1 to satisfy the API's @Min(1) constraint.
      const duration = staticDuration || (policyTerm.durationPropertyPath ? 1 : 0);

      terms.push({
        id: policyTerm.id ?? undefined,
        duration,
        durationPropertyPath: policyTerm.durationPropertyPath ?? null,
        paymentSchedules,
        paymentOptional: !!policyTerm.paymentOptional,
        autoRenew: !!policyTerm.autoRenew,
        // The limit only means something while Auto-Renew is on; never keep a hidden one.
        maxAutoRenewals: policyTerm.autoRenew ? this.toMaxAutoRenewals(policyTerm.maxAutoRenewals) : null,
        title: policyTerm.title,
        order: policyTerm.order,
      });
    }

    return terms;
  }

  /** The limit only applies while Auto-Renew is on; clear it so a hidden invalid value can't block saving. */
  onAutoRenewChange(term: FormGroup, autoRenew: boolean) {
    if (!autoRenew) term.get('maxAutoRenewals')?.setValue(null);
  }

  /** A blank limit means unlimited (null); number inputs can hand back an empty string. */
  private toMaxAutoRenewals(value: number | string | null | undefined): number | null {
    return value === null || value === undefined || value === '' ? null : Number(value);
  }

  private mapTermsToForm(terms: Policy.PolicyTerm[]) {
    if (terms.length > 0) {
      const expanded = terms.length < 2;
      this.terms = terms.map(term => {
        const years = Math.floor(term.duration / 365);
        const months = Math.floor((term.duration - 365 * years) / 30);
        const days = Math.floor(term.duration - 365 * years - 30 * months);

        return this.formBuilder.group({
          id: term.id,
          title: [term.title || null, [Validators.required]],
          years: [years, [Validators.min(0), wholeNumberValidator]],
          months: [months, [Validators.min(0), wholeNumberValidator]],
          days: [days, [Validators.min(0), wholeNumberValidator]],
          durationPropertyPath: [term.durationPropertyPath ?? null],
          order: [term.order as number | null, [Validators.required, Validators.min(0)]],
          paymentOptional: term.paymentOptional ?? false,
          autoRenew: term.autoRenew ?? false,
          maxAutoRenewals: [term.maxAutoRenewals ?? null, [Validators.min(0), wholeNumberValidator]],
          installmentsCheckBox: term.paymentSchedules.find(schedule => schedule.title !== 'One Time Payment')
            ? true
            : false,
          schedules: this.formBuilder.array(
            term.paymentSchedules
              .filter(schedule => schedule.title !== 'One Time Payment')
              .map(schedule => {
                return this.scheduleFormControl(schedule.installments, schedule.rate, schedule.title);
              })
          ),
          oneTimeCheckBox: term.paymentSchedules.find(schedule => schedule.title === 'One Time Payment') ? true : false,
          expanded,
        });
      });
    } else {
      this.terms = [];
      this.newTerm();
    }
  }

  navigateTo(path: string) {
    this.router.navigate([`${path}`], { relativeTo: this.route });
  }
}
